import React, { useState, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import { Plus, Edit2, Trash2, Check, X, CreditCard } from "lucide-react";
import Swal from 'sweetalert2';
import { API_BASE_URL } from "../config";
import axios from "axios";

export default function Subscriptions() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPlanId, setEditingPlanId] = useState(null);

  const [formData, setFormData] = useState({
    plan_name: "",
    price: "",
    duration_days: "30",
    status: "active",
    features: ""
  });

  const [plans, setPlans] = useState([]);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/plans/all`);
      if (response.data.status) {
        setPlans(response.data.data.map((p, idx) => {
          const colors = [
            "bg-slate-50 text-slate-700 border-slate-200 dark:border-slate-700",
            "bg-blue-50 text-blue-700 border-blue-200",
            "bg-emerald-50 text-emerald-700 border-emerald-200",
            "bg-purple-50 text-purple-700 border-purple-200"
          ];
          return {
            ...p,
            color: colors[idx % colors.length]
          };
        }));
      }
    } catch (error) {
      console.error("Error fetching plans:", error);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  const handleSave = async () => {
    if (!formData.plan_name.trim() || formData.price.toString().trim() === "") {
      Swal.fire("Error", "Plan name and price are required!", "error");
      return;
    }

    const payload = {
      plan_name: formData.plan_name,
      price: parseFloat(formData.price),
      duration_days: parseInt(formData.duration_days, 10),
      features: formData.features,
      status: formData.status
    };

    try {
      if (editingPlanId) {
        const response = await axios.put(`${API_BASE_URL}/api/plans/update/${editingPlanId}`, payload);
        if (response.data.status) {
          Swal.fire("Success", "Plan updated successfully!", "success");
          fetchPlans();
        } else {
          Swal.fire("Error", response.data.message || "Failed to update", "error");
        }
      } else {
        const response = await axios.post(`${API_BASE_URL}/api/plans/create`, payload);
        if (response.data.status) {
          Swal.fire("Success", "Plan created successfully!", "success");
          fetchPlans();
        } else {
          Swal.fire("Error", response.data.message || "Failed to create", "error");
        }
      }
      setIsModalOpen(false);
    } catch (error) {
      console.error("Error saving plan:", error);
      Swal.fire("Error", "Something went wrong", "error");
    }
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.delete(`${API_BASE_URL}/api/plans/delete/${id}`);
          setPlans(plans.filter(p => p.plan_id_PK !== id));
          Swal.fire('Deleted!', 'Plan has been deleted.', 'success');
        } catch (error) {
          Swal.fire('Error!', 'Failed to delete plan.', 'error');
        }
      }
    });
  };

  const openModal = (plan = null) => {
    if (plan) {
      setFormData({
        plan_name: plan.plan_name,
        price: plan.price.toString(),
        duration_days: plan.duration_days.toString(),
        status: plan.status,
        features: plan.features || ""
      });
      setEditingPlanId(plan.plan_id_PK);
    } else {
      setFormData({ plan_name: "", price: "", duration_days: "30", status: "active", features: "" });
      setEditingPlanId(null);
    }
    setIsModalOpen(true);
  };

  const formatDurationDays = (days) => {
    if (days == 1) return "1 Day";
    if (days == 7) return "1 Week";
    if (days == 30) return "1 Month";
    if (days == 365) return "1 Year";
    if (days >= 99990) return "Forever";
    return `${days} Days`;
  };

  return (
    <div className="bg-[#f8fafc] dark:bg-slate-900 min-h-screen text-slate-800 dark:text-slate-100 font-sans selection:bg-blue-100 dark:selection:bg-blue-900 relative overflow-hidden">
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <div className="flex-1 lg:ml-64 flex flex-col min-h-screen relative w-full lg:w-[calc(100%-16rem)]">
        <Header
          onMenuClick={() => setIsSidebarOpen(true)}
          title="Subscription Plans"
          subtitle="Manage app subscription tiers and their features."
        />

        <div className="p-4 sm:p-8 flex-1 w-full">
          <div className="mb-8 flex flex-col lg:flex-row lg:items-center justify-end gap-4">
            <button
              onClick={() => openModal()}
              className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm px-4 py-2 rounded-xl text-sm font-medium transition-all flex items-center justify-center gap-2">
              <Plus size={16} /> Add New Plan
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {plans.map((plan) => {
              const featureList = plan.features ? plan.features.split('\n') : [];
              return (
                <div key={plan.plan_id_PK} className={`rounded-2xl border bg-white dark:bg-slate-800 p-6 relative flex flex-col shadow-sm hover:shadow-md transition-shadow group`}>
                  <div className="flex justify-between items-start mb-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide border ${plan.color}`}>
                      {plan.plan_name}
                    </span>
                    <div className="flex items-center gap-2">
                      <button onClick={() => openModal(plan)} className="text-slate-500 hover:text-blue-600 p-1.5 bg-slate-100 hover:bg-blue-50 rounded-md transition-colors">
                        <Edit2 size={14} />
                      </button>
                      <button onClick={() => handleDelete(plan.plan_id_PK)} className="text-slate-500 hover:text-rose-600 p-1.5 bg-slate-100 hover:bg-rose-50 rounded-md transition-colors">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>

                  <div className="mb-6">
                    <span className="text-3xl font-bold text-slate-800 dark:text-slate-100">₹{plan.price}</span>
                    <span className="text-slate-500 dark:text-slate-400 text-sm ml-1">/{formatDurationDays(plan.duration_days)}</span>
                  </div>

                  <ul className="space-y-3 mb-6 flex-1">
                    {featureList.map((feature, index) => (
                      feature.trim() && (
                        <li key={index} className="flex items-start gap-2 text-sm text-slate-600">
                          <Check size={16} className="text-emerald-500 shrink-0 mt-0.5" />
                          <span>{feature}</span>
                        </li>)
                    ))}
                  </ul>

                  <div className="pt-4 border-t border-slate-100 dark:border-slate-700 flex items-center justify-between">
                    <span className={`px-2.5 py-1 rounded-md text-[10px] font-bold tracking-wide uppercase ${plan.status === 'active' ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-600'}`}>
                      {plan.status}
                    </span>
                    <span className="text-xs text-slate-400 font-medium">ID: {plan.plan_id_PK}</span>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-slate-900/40 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-md overflow-hidden border border-slate-200 dark:border-slate-700">
            <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between">
              <h3 className="font-bold text-slate-800 dark:text-slate-100">{editingPlanId ? 'Edit Subscription Plan' : 'Add Subscription Plan'}</h3>
              <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-slate-600 p-1">
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Plan Name</label>
                <input type="text" value={formData.plan_name} onChange={e => setFormData({ ...formData, plan_name: e.target.value })} className="w-full border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 outline-none focus:border-blue-500 text-sm" placeholder="e.g. Pro Plan" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Price (₹)</label>
                  <input type="number" value={formData.price} onChange={e => setFormData({ ...formData, price: e.target.value })} className="w-full border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 outline-none focus:border-blue-500 text-sm" placeholder="e.g. 499" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Duration</label>
                  <select value={formData.duration_days} onChange={e => setFormData({ ...formData, duration_days: e.target.value })} className="w-full border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 outline-none focus:border-blue-500 text-sm">
                    <option value="1">Day</option>
                    <option value="7">Week</option>
                    <option value="30">Month</option>
                    <option value="365">Year</option>
                    <option value="99999">Forever</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Features (One per line)</label>
                <textarea
                  rows={4}
                  value={formData.features}
                  onChange={e => setFormData({ ...formData, features: e.target.value })}
                  className="w-full border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 outline-none focus:border-blue-500 text-sm resize-none"
                  placeholder="No Ads&#10;Premium Templates&#10;No Watermark"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Status</label>
                <select value={formData.status} onChange={e => setFormData({ ...formData, status: e.target.value })} className="w-full border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 outline-none focus:border-blue-500 text-sm">
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-slate-100 dark:border-slate-700 flex items-center justify-end gap-3 bg-slate-50">
              <button onClick={() => setIsModalOpen(false)} className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-200 bg-slate-100 rounded-lg transition-colors">Cancel</button>
              <button onClick={handleSave} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm transition-colors">Save Plan</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
